import { useEffect, useState } from 'react';
import { Accordion, Button, Spinner, Table } from 'react-bootstrap';
import arrayUtilities from '../../../utilities/arrayUtilities';
import enums from '../../../utilities/enums';
import useApi from '../../../utilities/useApi';
import ErrorModal from '../../common/errorModal';
import { DataPagination } from '../../common/pagination';
import Loading from '../../common/loading';

const pollIntervalMs = 10000;
const pageSize = 10;

const isInProgress = (item) => item.status === 0 || item.status === 1; // Pending / Processing

export default function DownloadsToolbox(props) {

    const { api } = useApi();
    const [downloads, setDownloads] = useState(null);
    const [page, setPage] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [errorModal, setErrorModal] = useState(null);

    useEffect(() => {

        let timeout = null;
        let isCancelled = false;

        const fetchDownloads = () => {
            api.searchDownloads(props.organisationId, '', page, null,
                (data) => {
                    if (isCancelled) {
                        return;
                    }

                    setDownloads(data);
                    setIsLoading(false);
                    if (data.items?.some(isInProgress)) {
                        timeout = setTimeout(fetchDownloads, pollIntervalMs);
                    }
                },
                (error) => {
                    console.log(error);
                    setIsLoading(false);
                },
                props.projectId, pageSize
            );
        };

        // Only the initial / page-change fetch shows the spinner, not the background polls
        setIsLoading(true);
        fetchDownloads();

        return () => {
            isCancelled = true;
            clearTimeout(timeout);
        };
    }, [props.organisationId, props.projectId, props.refreshKey, page]);

    const download = (item) => {
        api.getDownload(item.organisationId, item.taskType, item.id, (result) => {
            if (result.url) {
                window.open(result.url, '_blank', 'noopener');
            } else {
                setErrorModal({ message: 'This file is not available to download.' });
            }
        }, (error) => setErrorModal(error));
    };

    return <Accordion.Item eventKey='10'>
        <Accordion.Header><i className='fi-download me-2' /> Downloads</Accordion.Header>
        <Accordion.Body>
            <div className='position-relative' style={{ minHeight: isLoading ? '4rem' : undefined }}>
                {isLoading &&
                    <div className='position-absolute top-0 start-0 w-100 h-100' style={{ zIndex: 4 }}>
                        <Loading blur opaque />
                    </div>
                }
                {downloads !== null && (arrayUtilities.isNullOrEmpty(downloads.items)
                    ? <p className='fs-sm text-muted mb-0'>No downloads for this project yet.</p>
                    : <>
                        <Table size='sm' className='fs-sm'>
                            <thead>
                                <tr>
                                    <th>Type</th>
                                    <th>Status</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {downloads.items.map(item => <tr key={item.id}>
                                    <td>{enums.ExportTaskType[item.taskType]}</td>
                                    <td>
                                        {isInProgress(item)
                                            ? <Spinner animation='border' size='sm' role='status'><span className='visually-hidden'>Loading</span></Spinner>
                                            : <span className={enums.ExportStatusTextClass[item.status]}>{enums.ExportStatus[item.status]}</span>}
                                    </td>
                                    <td>
                                        {item.status === 2 &&
                                            <Button variant='link' size='sm' className='p-0' onClick={() => download(item)} title='Download' aria-label='Download'><i className='fi-download'></i></Button>}
                                    </td>
                                </tr>)}
                            </tbody>
                        </Table>
                        <div className='d-flex justify-content-center'>
                            <DataPagination
                                totalResults={downloads.totalCount}
                                page={page}
                                onPageNumberClick={(pageNumber) => setPage(pageNumber)}
                                paginationSeedNumber={pageSize}
                                numberOfResultsPages={Math.ceil(downloads.totalCount / pageSize)}
                            />
                        </div>
                    </>
                )}
            </div>
            {errorModal &&
                <ErrorModal error={errorModal} onDismiss={() => setErrorModal(null)} />
            }
        </Accordion.Body>
    </Accordion.Item>
}
